import * as React from "react";

import { cn, normalizeStreet } from "@/lib/utils";
import { useDebounce } from "@/utils/misc";
import { MapPinCheck, SearchIcon } from "lucide-react";
import { useFetcher } from "react-router-dom";
import { Button } from "./button";
import { Command, CommandEmpty, CommandInput, CommandItem } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Input } from "./input";

export type Address = {
    street?: string;
    postal_code?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
};
interface AddressProps {
    address?: Address;
    onAddressChange?: (value: Address) => void;
    disabled?: boolean;
    className?: string;
    defaultSearch?: string;
}

export function AddressInput({ address, onAddressChange, className, disabled = false, defaultSearch }: AddressProps) {
    const fetcher = useFetcher<
        {
            label: string;
            housenumber: string;
            street: string;
            postcode: string;
            city: string;
            latitude: number;
            longitude: number;
            country: string;
        }[]
    >();
    const [manual, setManual] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState<string>();
    const [addressLabel, setAddressLabel] = React.useState<string>();
    const debouncedSearch = useDebounce(search, 600);

    React.useEffect(() => {
        if (debouncedSearch) {
            fetcher.load(`/utils/address/search?q=${debouncedSearch}`);
        }
    }, [debouncedSearch]);

    React.useEffect(() => {
        if (!address && defaultSearch) {
            setSearch(defaultSearch);
        }
    }, [defaultSearch]);

    React.useEffect(() => {
        if (address) {
            setAddressLabel([(address?.street || "") + (address?.street && address?.postal_code ? ", " : ""), address?.postal_code, address?.city || ""].filter(v => Boolean(v)).join(" "));
        } else {
            setAddressLabel(undefined);
        }
    }, [address]);

    return (
        <div className={cn("relative", className)}>
            {manual && (
                <p className=" absolute -top-6 right-0 py-1 text-right text-xs text-gray-500">
                    or use{" "}
                    <span
                        className="cursor-pointer underline"
                        onClick={() => {
                            setManual(false);
                            setOpen(true);
                        }}
                    >
                        the search tool
                    </span>
                </p>
            )}
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
                {!manual ? (
                    <div className="col-span-12">
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" role="combobox" className="w-full justify-start space-x-2" disabled={disabled}>
                                    {addressLabel ? (
                                        <>
                                            <MapPinCheck className="ml-2 h-4 w-4 shrink-0 opacity-50" /> <span>{addressLabel}</span>
                                        </>
                                    ) : (
                                        <>
                                            <SearchIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            <span>Search for an address</span>
                                        </>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className={cn("p-0", "w-[--radix-popover-trigger-width]")}>
                                <Command shouldFilter={false}>
                                    <CommandInput
                                        placeholder="Search for an address"
                                        value={search}
                                        onInput={e => {
                                            setSearch(e.currentTarget.value);
                                        }}
                                    />
                                    {(fetcher.data?.length || 0) > 0 ? (
                                        fetcher.data &&
                                        fetcher.data.map(a => (
                                            <CommandItem
                                                key={a.label}
                                                value={a.label}
                                                onSelect={() => {
                                                    if (onAddressChange) {
                                                        onAddressChange({
                                                            street: [a.housenumber, a.street].filter(v => Boolean(v)).join(" "),
                                                            postal_code: a.postcode,
                                                            city: a.city,
                                                            country: a.country,
                                                            latitude: a.latitude,
                                                            longitude: a.longitude
                                                        });
                                                    }
                                                    setOpen(false);
                                                }}
                                            >
                                                {a.label}
                                            </CommandItem>
                                        ))
                                    ) : fetcher.state != "idle" ? (
                                        <CommandEmpty>Searching...</CommandEmpty>
                                    ) : (search?.length || 0) == 0 ? (
                                        <CommandEmpty>Start typing to search</CommandEmpty>
                                    ) : (
                                        <CommandItem value="manual" className="cursor-pointer" onSelect={() => setManual(true)}>
                                            No results, click to enter manually
                                        </CommandItem>
                                    )}
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                ) : (
                    <>
                        <div className="lg:col-span-6">
                            <Input placeholder="Street" value={address?.street} onChange={e => onAddressChange && onAddressChange({ ...(address || {}), street: normalizeStreet(e.target.value) })} />
                        </div>
                        <div className="lg:col-span-3">
                            <Input placeholder="Postal Code" value={address?.postal_code} onChange={e => onAddressChange && onAddressChange({ ...(address || {}), postal_code: e.target.value })} />
                        </div>
                        <div className="lg:col-span-3">
                            <Input placeholder="City" value={address?.city} onChange={e => onAddressChange && onAddressChange({ ...(address || {}), city: e.target.value })} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
